import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { jobsAPI } from '../../services/api';
import BusinessLayout from '../../components/BusinessLayout';
import ConfirmModal from '../../components/ConfirmModal';
import toast from 'react-hot-toast';
import { 
  Briefcase, 
  Search, 
  Plus, 
  Eye, 
  Edit, 
  Trash2, 
  Users,
  Clock,
  MapPin,
  CheckCircle,
  XCircle,
  PauseCircle,
  MoreVertical
} from 'lucide-react';

interface Job {
  _id: string;
  title: string;
  description: string;
  employmentType: string;
  location: {
    city?: string;
    country?: string;
  };
  status: string;
  applications?: any[];
  createdAt: string;
  expiresAt?: string;
}

const EmployerJobs: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'danger' | 'warning' | 'info' | 'success';
    confirmText?: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'danger',
    onConfirm: () => {}
  });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, [statusFilter]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      
      const response = await jobsAPI.getMyJobs(params);
      setJobs(response.data.data || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter(job => {
    if (!search) return true;
    return job.title.toLowerCase().includes(search.toLowerCase()) ||
           job.description?.toLowerCase().includes(search.toLowerCase());
  });

  const handleStatusChange = (jobId: string, newStatus: string) => {
    setConfirmModal({
      isOpen: true,
      title: newStatus === 'closed' ? 'Close Job' : 'Reopen Job',
      message: newStatus === 'closed' 
        ? 'Are you sure you want to close this job? No new applications will be accepted.'
        : 'Are you sure you want to reopen this job?',
      type: newStatus === 'closed' ? 'warning' : 'success',
      confirmText: newStatus === 'closed' ? 'Close' : 'Reopen',
      onConfirm: async () => {
        setProcessing(true);
        try {
          await jobsAPI.update(jobId, { status: newStatus });
          toast.success(`Job ${newStatus === 'closed' ? 'closed' : 'reopened'} successfully`);
          setConfirmModal({ ...confirmModal, isOpen: false });
          fetchJobs();
        } catch (error) {
          toast.error('Failed to update job status');
        } finally {
          setProcessing(false);
        }
      }
    });
  };

  const handleDelete = (jobId: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Job',
      message: 'Are you sure you want to delete this job? This action cannot be undone.',
      type: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        setProcessing(true);
        try {
          await jobsAPI.delete(jobId);
          toast.success('Job deleted successfully');
          setConfirmModal({ ...confirmModal, isOpen: false });
          fetchJobs();
        } catch (error) {
          toast.error('Failed to delete job');
        } finally {
          setProcessing(false);
        }
      }
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700 border-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'draft': return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'closed': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <BusinessLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Jobs</h1>
            <p className="text-gray-500 mt-1">Manage your job postings</p>
          </div>
          <Link
            to="/employer/post-job"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Post New Job
          </Link>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search jobs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="draft">Draft</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No jobs found</p>
            <Link to="/employer/post-job" className="text-blue-600 hover:underline text-sm mt-2 inline-block">
              Post your first job
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredJobs.map((job) => (
              <div key={job._id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{job.title}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(job.status)}`}>
                        {job.status}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                      <span className="flex items-center gap-1">
                        <Briefcase className="w-4 h-4" />
                        {job.employmentType}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {job.location?.city}, {job.location?.country}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {job.applications?.length || 0} applications
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Posted {new Date(job.createdAt).toLocaleDateString()}
                      </span>
                    </div>

                    <p className="text-gray-500 text-sm line-clamp-2">{job.description}</p>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <Link
                      to={`/jobs/${job._id}`}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="View Job"
                    >
                      <Eye className="w-5 h-5" />
                    </Link>
                    <Link
                      to={`/post-job?edit=${job._id}`}
                      className="p-2 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                      title="Edit Job"
                    >
                      <Edit className="w-5 h-5" />
                    </Link>
                    {job.status === 'active' ? (
                      <button
                        onClick={() => handleStatusChange(job._id, 'closed')}
                        className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                        title="Close Job"
                      >
                        <PauseCircle className="w-5 h-5" />
                      </button>
                    ) : job.status === 'closed' ? (
                      <button
                        onClick={() => handleStatusChange(job._id, 'active')}
                        className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Reopen Job"
                      >
                        <CheckCircle className="w-5 h-5" />
                      </button>
                    ) : null}
                    <button
                      onClick={() => handleDelete(job._id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete Job"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          type={confirmModal.type}
          confirmText={confirmModal.confirmText}
          loading={processing}
        />
      </div>
    </BusinessLayout>
  );
};

export default EmployerJobs;
